import React from 'react';
import Input from '../ui/input';
import cn from '../../lib/utils';

/**
 * Interface representing the props for the FormInput component.
 *
 * - title - The label to display
 * - hint - An optional hint or description displayed below the title.
 * - id - The unique identifier for the input field.
 * - mandatory - Indicates whether the input field is required. Defaults to true.
 * - val - The current value of the input field.
 * - setState - Callback function to update the state with the input field's value.
 * - err - An optional error message to display if there is an error with the input.
 */
interface FormInputProps {
  title: string;
  hint?: string;
  id: string;
  mandatory?: boolean;
  val: string;
  setState: (value: string) => void;
  err?: string;
}

/**
 * FormInput component that renders a labeled text input field with optional hint and error message.
 * It wraps the Shadcn UI Input component with form-specific functionality like labels and validation.
 *
 * @param title The label for the input field.
 * @param hint Optional hint or description for the input field.
 * @param id The unique identifier for the input field.
 * @param mandatory Indicates if the input field is required. Defaults to true.
 * @param val The current value of the input field.
 * @param setState Callback function to update the value of the input field.
 * @param err Optional error message to display below the input field.
 */
const FormInput = ({ title, hint, id, mandatory = true, val, setState, err }: FormInputProps) => (
  <div className='space-y-2'>
    <div className='text-sm font-medium'>
      {title}
      {mandatory ? <span className='text-red-500 ml-1'>*</span> : ''}
    </div>
    {hint && <div className='text-xs text-gray-500 italic ml-2'>{hint}</div>}
    <Input
      id={id}
      className={cn('w-full', err && 'border-red-500 focus-visible:ring-red-500')}
      type='text'
      value={val}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setState(e.target.value)}
    />
    {err && <div className='text-sm text-red-500'>{err}</div>}
  </div>
);

export default FormInput;
